import { useEffect } from "react";
import {
  ArrowRight,
  ArrowUpRight,
  Building2,
  CheckCircle2,
  ClipboardCheck,
  Home,
  Layers3,
  Mail,
  Phone,
  ShieldCheck,
  Siren,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { PortfolioGallery } from "@/components/site/PortfolioGallery";
import { Breadcrumbs, PageHero } from "@/components/site/PageHero";
import { AboutBrands } from "@/components/site/AboutBrands";
import { AboutCredentials } from "@/components/site/AboutCredentials";
import { AboutGuarantee } from "@/components/site/AboutGuarantee";
import { Faq } from "@/components/site/Faq";
import { QuoteForm } from "@/components/site/QuoteForm";
import { business } from "@/config/business";
import { images } from "@/config/images";
import {
  SERVICE_CATEGORY_LABELS,
  SERVICE_CATEGORY_ORDER,
  publishedServicePages,
  type ServiceCategoryId,
} from "@/config/services";
import { useI18n } from "@/i18n";
import { trackAnalyticsEvent } from "@/lib/analytics";
import { localizeServicePage, type PageUi } from "@/i18n/content";
import { galleryCopyEn } from "@/i18n/gallery-copy";
import type { LocaleCode } from "@/i18n/routing";
import { getMarketingCopy } from "@/i18n/marketing-copy";

export { ProjectDetailPage } from "@/components/pages/ProjectDetailPage";

type PathBuilder = (href: string) => string;

const SERVICE_HUBS = [
  { path: "/emergency-roofing", key: "emergency", icon: Siren },
  { path: "/residential-roofing", key: "residential", icon: Home },
  { path: "/commercial-roofing", key: "commercial", icon: Building2 },
  { path: "/strata-roofing", key: "strata", icon: ShieldCheck },
  { path: "/roof-systems", key: "roofSystems", icon: Layers3 },
] as const;

const SERVICE_CATALOGUE_COPY = {
  "en-ca": {
    pathwaysEyebrow: "Explore by pathway",
    pathwaysTitle: "Start with the property or roof system",
    pathwaysBody:
      "These hubs organize Revolin’s published services around the building and the decisions owners need to make.",
    hubs: {
      emergency: "Emergency roofing",
      residential: "Residential roofing",
      commercial: "Commercial roofing",
      strata: "Strata roofing",
      roofSystems: "Roof systems",
    },
    viewPathway: "View pathway",
    published: "Published services",
    available: "Available now",
    plannedEyebrow: "Planned catalogue",
    plannedTitle: "Capabilities under review",
    plannedBody:
      "The following catalogue areas are planned, not published service offerings. They are shown for planning context only; scope and availability must be confirmed directly with Revolin.",
    plannedStatus: "Planned · contact to confirm",
    plannedCount: "planned capabilities",
    contact: "Contact us to confirm your scope",
  },
  "zh-cn": {
    pathwaysEyebrow: "按路径浏览",
    pathwaysTitle: "从物业类型或屋顶系统开始",
    pathwaysBody: "这些入口页按建筑类型和业主需要做的决策，整理 Revolin 已正式发布的服务。",
    hubs: {
      emergency: "紧急屋顶服务",
      residential: "住宅屋顶",
      commercial: "商业屋顶",
      strata: "共管物业屋顶",
      roofSystems: "屋顶系统",
    },
    viewPathway: "查看路径",
    published: "已发布服务",
    available: "现可预约",
    plannedEyebrow: "规划中的目录",
    plannedTitle: "仍在评估的能力",
    plannedBody:
      "以下目录领域仍在规划中，尚未作为可预约服务发布。此处仅作规划参考；工程范围和能否承接须直接与 Revolin 确认。",
    plannedStatus: "规划中 · 请联系确认",
    plannedCount: "项规划中能力",
    contact: "联系我们确认工程范围",
  },
  "zh-tw": {
    pathwaysEyebrow: "按路徑瀏覽",
    pathwaysTitle: "從物業類型或屋頂系統開始",
    pathwaysBody: "這些入口頁按建築類型和業主需要做的決策，整理 Revolin 已正式發布的服務。",
    hubs: {
      emergency: "緊急屋頂服務",
      residential: "住宅屋頂",
      commercial: "商業屋頂",
      strata: "共管物業屋頂",
      roofSystems: "屋頂系統",
    },
    viewPathway: "查看路徑",
    published: "已發布服務",
    available: "現可預約",
    plannedEyebrow: "規劃中的目錄",
    plannedTitle: "仍在評估的能力",
    plannedBody:
      "以下目錄領域仍在規劃中，尚未作為可預約服務發布。此處僅作規劃參考；工程範圍及能否承接須直接與 Revolin 確認。",
    plannedStatus: "規劃中 · 請聯繫確認",
    plannedCount: "項規劃中能力",
    contact: "聯繫我們確認工程範圍",
  },
  "pa-in": {
    pathwaysEyebrow: "ਰਸਤੇ ਅਨੁਸਾਰ ਵੇਖੋ",
    pathwaysTitle: "ਪ੍ਰਾਪਰਟੀ ਜਾਂ ਛੱਤ ਪ੍ਰਣਾਲੀ ਤੋਂ ਸ਼ੁਰੂ ਕਰੋ",
    pathwaysBody:
      "ਇਹ ਹੱਬ Revolin ਦੀਆਂ ਪ੍ਰਕਾਸ਼ਿਤ ਸੇਵਾਵਾਂ ਨੂੰ ਇਮਾਰਤ ਅਤੇ ਮਾਲਕਾਂ ਵੱਲੋਂ ਲਏ ਜਾਣ ਵਾਲੇ ਫ਼ੈਸਲਿਆਂ ਅਨੁਸਾਰ ਵਿਵਸਥਿਤ ਕਰਦੇ ਹਨ।",
    hubs: {
      emergency: "ਐਮਰਜੈਂਸੀ ਛੱਤ ਸੇਵਾ",
      residential: "ਰਿਹਾਇਸ਼ੀ ਛੱਤਾਂ",
      commercial: "ਵਪਾਰਕ ਛੱਤਾਂ",
      strata: "ਸਟ੍ਰਾਟਾ ਛੱਤਾਂ",
      roofSystems: "ਛੱਤ ਪ੍ਰਣਾਲੀਆਂ",
    },
    viewPathway: "ਰਸਤਾ ਵੇਖੋ",
    published: "ਪ੍ਰਕਾਸ਼ਿਤ ਸੇਵਾਵਾਂ",
    available: "ਹੁਣ ਉਪਲਬਧ",
    plannedEyebrow: "ਯੋਜਨਾਬੱਧ ਸੂਚੀ",
    plannedTitle: "ਸਮੀਖਿਆ ਅਧੀਨ ਸਮਰੱਥਾਵਾਂ",
    plannedBody:
      "ਹੇਠਲੇ ਖੇਤਰ ਯੋਜਨਾਬੱਧ ਹਨ, ਪ੍ਰਕਾਸ਼ਿਤ ਸੇਵਾਵਾਂ ਨਹੀਂ। ਇਹ ਸਿਰਫ਼ ਯੋਜਨਾ ਸੰਦਰਭ ਲਈ ਦਿਖਾਏ ਗਏ ਹਨ; ਕੰਮ ਦਾ ਦਾਇਰਾ ਅਤੇ ਉਪਲਬਧਤਾ Revolin ਨਾਲ ਸਿੱਧੀ ਪੁਸ਼ਟੀ ਕਰਨੀ ਚਾਹੀਦੀ ਹੈ।",
    plannedStatus: "ਯੋਜਨਾਬੱਧ · ਪੁਸ਼ਟੀ ਲਈ ਸੰਪਰਕ ਕਰੋ",
    plannedCount: "ਯੋਜਨਾਬੱਧ ਸਮਰੱਥਾਵਾਂ",
    contact: "ਕੰਮ ਦਾ ਦਾਇਰਾ ਪੱਕਾ ਕਰਨ ਲਈ ਸੰਪਰਕ ਕਰੋ",
  },
  "ko-kr": {
    pathwaysEyebrow: "경로별로 살펴보기",
    pathwaysTitle: "건물 유형 또는 지붕 시스템부터 시작하세요",
    pathwaysBody:
      "이 허브는 Revolin이 공개한 서비스를 건물 유형과 소유주가 내려야 하는 결정 기준으로 정리합니다.",
    hubs: {
      emergency: "긴급 지붕 서비스",
      residential: "주택 지붕",
      commercial: "상업용 지붕",
      strata: "스트라타 지붕",
      roofSystems: "지붕 시스템",
    },
    viewPathway: "경로 보기",
    published: "공개된 서비스",
    available: "현재 이용 가능",
    plannedEyebrow: "계획 중인 목록",
    plannedTitle: "검토 중인 역량",
    plannedBody:
      "아래 항목은 계획 중이며 아직 예약 가능한 공개 서비스가 아닙니다. 계획 참고용으로만 표시되며, 공사 범위와 가능 여부는 Revolin에 직접 확인해야 합니다.",
    plannedStatus: "계획 중 · 확인을 위해 문의",
    plannedCount: "개 계획 중 역량",
    contact: "공사 범위 확인을 위해 문의하기",
  },
  "fa-ir": {
    pathwaysEyebrow: "مسیرهای خدمات",
    pathwaysTitle: "بر اساس نوع ملک یا سیستم سقف شروع کنید",
    pathwaysBody:
      "این صفحات، خدمات منتشرشدهٔ رولین را بر پایهٔ نوع ساختمان و تصمیم‌های موردنیاز مالک دسته‌بندی می‌کنند.",
    hubs: {
      emergency: "خدمات فوری سقف",
      residential: "سقف ساختمان‌های مسکونی",
      commercial: "سقف ساختمان‌های تجاری",
      strata: "سقف مجتمع‌های استراتا",
      roofSystems: "سیستم‌های سقف",
    },
    viewPathway: "مشاهدهٔ مسیر خدمات",
    published: "خدمات منتشرشده",
    available: "در حال ارائه",
    plannedEyebrow: "فهرست برنامه‌ریزی‌شده",
    plannedTitle: "قابلیت‌های در دست بررسی",
    plannedBody:
      "موارد زیر هنوز به‌عنوان خدمت قابل سفارش منتشر نشده‌اند و فقط ساختار برنامه‌ریزی آینده را نشان می‌دهند. دامنهٔ کار و امکان ارائه باید مستقیماً با رولین تأیید شود.",
    plannedStatus: "برنامه‌ریزی‌شده · نیازمند تأیید",
    plannedCount: "قابلیت در دست بررسی",
    contact: "برای تأیید دامنهٔ پروژه تماس بگیرید",
  },
  "ar-sa": {
    pathwaysEyebrow: "مسارات الخدمات",
    pathwaysTitle: "ابدأ بنوع العقار أو نظام السقف",
    pathwaysBody:
      "تنظّم هذه الصفحات خدمات ريفولين المنشورة وفق نوع المبنى والقرارات التي يحتاج المالك إلى اتخاذها.",
    hubs: {
      emergency: "خدمات الأسقف العاجلة",
      residential: "الأسقف السكنية",
      commercial: "الأسقف التجارية",
      strata: "أسقف مجمعات الستراتا",
      roofSystems: "أنظمة الأسقف",
    },
    viewPathway: "عرض مسار الخدمات",
    published: "الخدمات المنشورة",
    available: "متاحة حالياً",
    plannedEyebrow: "الفهرس المخطط",
    plannedTitle: "قدرات قيد المراجعة",
    plannedBody:
      "المجالات التالية ليست خدمات منشورة متاحة للطلب بعد، وإنما تعرض هيكل التخطيط المستقبلي فقط. يجب تأكيد نطاق العمل والتوافر مباشرةً مع ريفولين.",
    plannedStatus: "مخطط لها · يلزم التأكيد",
    plannedCount: "قدرة قيد المراجعة",
    contact: "اتصل بنا لتأكيد نطاق مشروعك",
  },
} as const;

const CATEGORY_COPY: Partial<Record<LocaleCode, Partial<Record<ServiceCategoryId, string>>>> = {
  "zh-cn": {
    "emergency-repairs": "紧急维修",
    replacement: "屋顶更换",
    maintenance: "检查与维护",
    "gutters-exterior": "天沟与屋顶外沿部件",
    "commercial-strata": "商业与共管物业",
    installation: "屋顶新装",
    "roof-conversions": "屋顶系统转换",
    "roof-systems": "其他屋顶系统",
    "roofing-components": "屋顶专用构件",
    cedar: "雪松屋顶",
    storm: "风暴屋顶服务",
  },
  "zh-tw": {
    "emergency-repairs": "緊急維修",
    replacement: "屋頂更換",
    maintenance: "檢查與保養",
    "gutters-exterior": "天溝與屋頂外沿部件",
    "commercial-strata": "商業與共管物業",
    installation: "屋頂新裝",
    "roof-conversions": "屋頂系統轉換",
    "roof-systems": "其他屋頂系統",
    "roofing-components": "屋頂專用構件",
    cedar: "雪松屋頂",
    storm: "風暴屋頂服務",
  },
  "pa-in": {
    "emergency-repairs": "ਐਮਰਜੈਂਸੀ ਅਤੇ ਮੁਰੰਮਤ",
    replacement: "ਛੱਤ ਬਦਲੀ",
    maintenance: "ਜਾਂਚ ਅਤੇ ਰੱਖ-ਰਖਾਅ",
    "gutters-exterior": "ਗਟਰ ਅਤੇ ਬਾਹਰੀ ਛੱਤ ਹਿੱਸੇ",
    "commercial-strata": "ਵਪਾਰਕ ਅਤੇ ਸਟ੍ਰਾਟਾ",
    installation: "ਨਵੀਂ ਛੱਤ ਲਗਾਉਣਾ",
    "roof-conversions": "ਛੱਤ ਪ੍ਰਣਾਲੀ ਬਦਲਣਾ",
    "roof-systems": "ਵਾਧੂ ਛੱਤ ਪ੍ਰਣਾਲੀਆਂ",
    "roofing-components": "ਛੱਤ ਦੇ ਵਿਸ਼ੇਸ਼ ਹਿੱਸੇ",
    cedar: "ਸੀਡਰ ਛੱਤਾਂ",
    storm: "ਤੂਫ਼ਾਨੀ ਛੱਤ ਸੇਵਾਵਾਂ",
  },
  "ko-kr": {
    "emergency-repairs": "긴급 보수",
    replacement: "지붕 교체",
    maintenance: "점검 및 유지관리",
    "gutters-exterior": "홈통과 외장 지붕 부재",
    "commercial-strata": "상업용 및 스트라타",
    installation: "지붕 신규 시공",
    "roof-conversions": "지붕 시스템 전환",
    "roof-systems": "추가 지붕 시스템",
    "roofing-components": "지붕 전용 부재",
    cedar: "시더 지붕",
    storm: "폭풍 피해 지붕 서비스",
  },
  "fa-ir": {
    "emergency-repairs": "خدمات فوری و تعمیرات",
    replacement: "تعویض و اجرای کامل سقف",
    maintenance: "بازرسی و نگهداری",
    "gutters-exterior": "ناودان و اجزای بیرونی سقف",
    "commercial-strata": "سقف تجاری و استراتا",
    installation: "اجرای سقف جدید",
    "roof-conversions": "تغییر سیستم سقف",
    "roof-systems": "سیستم‌های تکمیلی سقف",
    "roofing-components": "اجزای تخصصی سقف",
    cedar: "سقف چوب سدر",
    storm: "خدمات خسارت طوفان",
  },
  "ar-sa": {
    "emergency-repairs": "الطوارئ والإصلاحات",
    replacement: "استبدال السقف وأنظمته الكاملة",
    maintenance: "الفحص والصيانة",
    "gutters-exterior": "المزاريب ومكونات حواف السقف",
    "commercial-strata": "الأسقف التجارية والستراتا",
    installation: "تركيب الأسقف الجديدة",
    "roof-conversions": "تحويل أنظمة الأسقف",
    "roof-systems": "أنظمة أسقف إضافية",
    "roofing-components": "مكونات الأسقف المتخصصة",
    cedar: "أسقف خشب الأرز",
    storm: "خدمات أضرار العواصف",
  },
};

function serviceCatalogueCopy(locale: LocaleCode) {
  return SERVICE_CATALOGUE_COPY[locale] ?? SERVICE_CATALOGUE_COPY["en-ca"];
}

function categoryLabel(
  category: ServiceCategoryId,
  locale: LocaleCode,
  localizedMarketingLabel?: string,
) {
  return (
    CATEGORY_COPY[locale]?.[category] ??
    localizedMarketingLabel ??
    SERVICE_CATEGORY_LABELS[category]
  );
}

export function ServicesIndexPage({
  path,
  ui,
  locale = "en-ca",
}: {
  path: PathBuilder;
  ui?: PageUi | undefined;
  locale?: LocaleCode;
}) {
  const copy = ui?.servicesIndex;
  const marketing = getMarketingCopy(locale);
  const catalogueCopy = serviceCatalogueCopy(locale);
  const localizedServices = publishedServicePages.map((service) =>
    localizeServicePage(service, locale),
  );
  const number = new Intl.NumberFormat(locale);
  let serviceNumber = 0;
  return (
    <>
      <PageHero
        eyebrow={copy?.eyebrow ?? "Roofing services"}
        title={copy?.title ?? "The right scope begins with the right diagnosis"}
        body={
          copy?.body ??
          "Explore Revolin Roofing’s service catalogue. Each page begins with the building, the roof assembly and the conditions we can observe - not a predetermined product recommendation."
        }
        breadcrumbs={
          <Breadcrumbs
            items={[
              { label: ui?.common.home ?? "Home", href: path("/") },
              { label: ui?.common.services ?? "Services" },
            ]}
          />
        }
      />
      <section className="border-b border-border bg-card py-10 md:py-14">
        <div className="container-page">
          <div className="grid gap-8 lg:grid-cols-[0.7fr_1.3fr] lg:items-end">
            <div>
              <p className="eyebrow">{catalogueCopy.pathwaysEyebrow}</p>
              <h2 className="display-3 mt-3 max-w-xl text-balance">
                {catalogueCopy.pathwaysTitle}
              </h2>
              <p className="mt-4 max-w-xl text-sm leading-7 text-muted-foreground">
                {catalogueCopy.pathwaysBody}
              </p>
            </div>
            <nav
              aria-label={catalogueCopy.pathwaysTitle}
              className="grid gap-px overflow-hidden rounded-xl border border-border bg-border sm:grid-cols-2"
            >
              {SERVICE_HUBS.map(({ path: hubPath, key, icon: Icon }) => (
                <a
                  key={key}
                  href={path(hubPath)}
                  className="group flex min-h-28 items-center gap-4 bg-background p-5 transition-colors hover:bg-muted/60"
                >
                  <span className="grid size-11 shrink-0 place-items-center rounded-full border border-accent/35 bg-accent/10 text-accent">
                    <Icon className="size-5" aria-hidden="true" />
                  </span>
                  <span className="min-w-0">
                    <strong className="block text-base">{catalogueCopy.hubs[key]}</strong>
                    <span className="mt-1.5 inline-flex items-center gap-1.5 text-xs font-semibold text-muted-foreground group-hover:text-foreground">
                      {catalogueCopy.viewPathway}
                      <ArrowRight className="size-3.5" aria-hidden="true" />
                    </span>
                  </span>
                </a>
              ))}
            </nav>
          </div>
        </div>
      </section>
      <section className="section-y">
        <div className="container-page space-y-16">
          <div className="flex flex-wrap items-center justify-between gap-4 border-b border-border pb-5">
            <div className="flex items-center gap-3">
              <span className="grid size-9 place-items-center rounded-full bg-emerald-500/10 text-emerald-700 dark:text-emerald-300">
                <CheckCircle2 className="size-4" aria-hidden="true" />
              </span>
              <p className="text-sm font-semibold">{catalogueCopy.published}</p>
            </div>
            <p className="text-sm tabular-nums text-muted-foreground">
              {number.format(publishedServicePages.length)} {catalogueCopy.available}
            </p>
          </div>
          {SERVICE_CATEGORY_ORDER.map((categoryId) => {
            const categoryServices = localizedServices.filter(
              (service) => service.category === categoryId,
            );
            if (!categoryServices.length) return null;
            return (
              <div key={categoryId}>
                <p className="eyebrow">
                  {marketing?.serviceCategories[categoryId] ?? SERVICE_CATEGORY_LABELS[categoryId]}
                </p>
                <h2 className="display-3 mt-3">
                  {marketing?.serviceCategories[categoryId] ?? SERVICE_CATEGORY_LABELS[categoryId]}
                </h2>
                <div className="mt-8 grid gap-6 lg:grid-cols-2">
                  {categoryServices.map((service) => {
                    serviceNumber += 1;
                    const image = images[service.image];
                    return (
                      <article
                        key={service.slug}
                        className="group grid overflow-hidden rounded-xl border border-border bg-card shadow-sm transition duration-500 hover:-translate-y-1 hover:border-accent/60 hover:shadow-card sm:grid-cols-[0.42fr_0.58fr]"
                      >
                        <div className="relative min-h-56 overflow-hidden sm:min-h-full">
                          <img
                            src={image.src}
                            alt={image.alt}
                            width={image.width}
                            height={image.height}
                            loading="lazy"
                            className="absolute inset-0 size-full object-cover transition duration-700 group-hover:scale-[1.04]"
                          />
                          <span className="absolute top-4 left-4 grid size-9 place-items-center rounded-full bg-accent font-semibold text-accent-foreground shadow-elev">
                            {String(serviceNumber).padStart(2, "0")}
                          </span>
                        </div>
                        <div className="p-7 md:p-8">
                          <div className="flex flex-wrap items-center justify-between gap-3">
                            <p className="eyebrow">{service.eyebrow}</p>
                            <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/25 bg-emerald-500/8 px-2.5 py-1 text-[0.65rem] font-semibold tracking-[0.08em] text-emerald-800 uppercase dark:text-emerald-200">
                              <CheckCircle2 className="size-3" aria-hidden="true" />
                              {catalogueCopy.available}
                            </span>
                          </div>
                          <h3 className="mt-3 text-2xl font-semibold">{service.title}</h3>
                          <p className="mt-4 text-sm leading-7 text-muted-foreground">
                            {service.description}
                          </p>
                          <a
                            href={path(service.path)}
                            className="mt-6 inline-flex items-center gap-2 text-sm font-semibold underline-offset-4 hover:underline"
                          >
                            {copy?.exploreService ?? "Explore service"}{" "}
                            <ArrowUpRight className="size-4" aria-hidden="true" />
                          </a>
                        </div>
                      </article>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </section>
      <section className="border-y border-border bg-muted/50 py-12">
        <div className="container-page grid gap-8 lg:grid-cols-[0.7fr_1.3fr] lg:items-start">
          <div>
            <p className="eyebrow">{copy?.technicalContext ?? "Technical context"}</p>
            <h2 className="display-3 mt-3">{copy?.sourcesTitle ?? "Current BC sources matter"}</h2>
          </div>
          <div className="grid gap-5 text-sm leading-7 text-muted-foreground md:grid-cols-3">
            <a
              href="https://rpm.rcabc.org/index.php/Main_Page_Div"
              rel="noopener"
              className="rounded-xl border border-border bg-card p-5 transition hover:border-accent hover:text-foreground"
            >
              <strong className="block text-foreground">
                {copy?.roofingManual ?? "RCABC Roofing Practices Manual"}
              </strong>
              {copy?.roofingManualBody ??
                "Current roofing-practice reference; a link does not imply Revolin membership or a RoofStar guarantee."}
            </a>
            <a
              href="https://www2.gov.bc.ca/gov/content/industry/construction-industry/building-codes-standards/bc-codes"
              rel="noopener"
              className="rounded-xl border border-border bg-card p-5 transition hover:border-accent hover:text-foreground"
            >
              <strong className="block text-foreground">{copy?.bcCodes ?? "BC Codes"}</strong>
              {copy?.bcCodesBody ??
                "Provincial code, revisions and bulletins for the jurisdiction and project scope."}
            </a>
            <a
              href="https://vancouver.ca/your-government/vancouver-building-bylaw.aspx"
              rel="noopener"
              className="rounded-xl border border-border bg-card p-5 transition hover:border-accent hover:text-foreground"
            >
              <strong className="block text-foreground">
                {copy?.vancouverBylaw ?? "Vancouver Building By-law"}
              </strong>
              {copy?.vancouverBylawBody ??
                "Vancouver-specific requirements are checked separately from the provincial code."}
            </a>
          </div>
        </div>
      </section>
      <AssessmentSection ui={ui} />
    </>
  );
}

export function AboutPage({ path, ui }: { path: PathBuilder; ui?: PageUi | undefined }) {
  const copy = ui?.corePages.about;
  return (
    <>
      <PageHero
        eyebrow={copy?.eyebrow ?? "A Revolin Group company"}
        title={copy?.title ?? "Roofing expertise with a construction company’s perspective"}
        body={
          copy?.body ??
          "Revolin Roofing is the specialized roofing division of Revolin Group, bringing the parent brand’s architectural restraint, transparent process and disciplined project thinking to the roof assembly."
        }
        breadcrumbs={
          <Breadcrumbs
            items={[
              { label: ui?.common.home ?? "Home", href: path("/") },
              { label: copy?.eyebrow ?? "About" },
            ]}
          />
        }
      />
      <section className="section-y">
        <div className="container-page grid gap-14 lg:grid-cols-[0.9fr_1.1fr] lg:gap-20">
          <div>
            <p className="eyebrow">{copy?.relationship ?? "The relationship"}</p>
            <h2 className="display-2 mt-3 text-balance">
              {copy?.relationshipTitle ??
                "Specialized in roofing. Connected to the whole building."}
            </h2>
          </div>
          <div className="space-y-6 text-base leading-8 text-muted-foreground">
            {(
              copy?.relationshipBody ?? [
                "A roof is not an isolated surface. It meets walls, cladding, skylights, ventilation, drainage and interior assemblies. Revolin Roofing approaches those interfaces with the broader construction context in view.",
                "That perspective supports clearer diagnosis and scope. It does not replace the need for the appropriate engineer, designer or specialist when a condition falls outside roofing work; it helps identify those boundaries earlier.",
              ]
            ).map((paragraph) => (
              <p key={paragraph}>{paragraph}</p>
            ))}
          </div>
        </div>
      </section>
      <section className="section-y border-y border-border bg-muted/50">
        <div className="container-page">
          <p className="eyebrow">{copy?.principles ?? "Working principles"}</p>
          <h2 className="display-2 mt-3 max-w-3xl text-balance">
            {copy?.principlesTitle ?? "Clarity at each decision point"}
          </h2>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {(
              copy?.principleItems ?? [
                {
                  icon: ClipboardCheck,
                  title: "Assess before recommending",
                  body: "The visible symptom, likely source and wider assembly condition are considered before a scope is proposed.",
                },
                {
                  icon: Building2,
                  title: "Respect the architecture",
                  body: "Material, colour, profile, edges and transitions contribute to how the building reads - not only how water sheds.",
                },
                {
                  icon: ShieldCheck,
                  title: "State the limits",
                  body: "Assumptions, exclusions and conditions that require further investigation belong in the conversation and the scope.",
                },
              ]
            ).map(({ title, body }, index) => {
              const Icon = [ClipboardCheck, Building2, ShieldCheck][index] ?? ClipboardCheck;
              return (
                <article key={title} className="rounded-xl border border-border bg-card p-8">
                  <span className="grid size-11 place-items-center rounded-full bg-accent/10 text-accent ring-1 ring-accent/25">
                    <Icon className="size-5" aria-hidden="true" />
                  </span>
                  <h3 className="mt-6 text-xl font-semibold">{title}</h3>
                  <p className="mt-3 text-sm leading-7 text-muted-foreground">{body}</p>
                </article>
              );
            })}
          </div>
        </div>
      </section>
      <AboutCredentials />
      <AboutGuarantee />
      <AboutBrands />
      <section className="section-y">
        <div className="container-page rounded-2xl bg-primary px-7 py-12 text-primary-foreground md:px-12 md:py-16">
          <p className="eyebrow">{copy?.parentCompany ?? "Parent company"}</p>
          <h2 className="display-2 mt-3">{copy?.parentTitle ?? "Explore Revolin Group"}</h2>
          <p className="mt-4 max-w-2xl leading-7 text-primary-foreground/70">
            {copy?.parentBody ??
              "For renovation, construction and broader building work, visit the parent company site."}
          </p>
          <Button variant="accent" size="xl" asChild className="mt-7">
            <a href={business.parentUrl} rel="noopener">
              {copy?.parentLink ?? "Visit RevolinGroup.com"} <ArrowUpRight aria-hidden="true" />
            </a>
          </Button>
        </div>
      </section>
    </>
  );
}

export function BeforeAfterGalleryPage({
  path,
  ui,
}: {
  path: PathBuilder;
  ui?: PageUi | undefined;
}) {
  const { locale } = useI18n();
  const copy = ui?.corePages.gallery;

  useEffect(() => {
    trackAnalyticsEvent({
      event: "project_gallery_view",
      locale,
    });
  }, [locale]);

  return (
    <>
      <PageHero
        eyebrow={copy?.eyebrow ?? galleryCopyEn.eyebrow}
        title={copy?.title ?? galleryCopyEn.title}
        body={copy?.body ?? galleryCopyEn.body}
        breadcrumbs={
          <Breadcrumbs
            items={[
              { label: ui?.common.home ?? "Home", href: path("/") },
              { label: copy?.eyebrow ?? galleryCopyEn.eyebrow },
            ]}
          />
        }
      />
      <PortfolioGallery path={path} />
      <section className="pb-20 md:pb-28">
        <div className="container-page rounded-xl border border-border bg-muted/30 p-7 text-sm leading-7 text-muted-foreground">
          {copy?.notice ?? galleryCopyEn.notice}
        </div>
      </section>
    </>
  );
}

export function ThankYouPage({ path, ui }: { path: PathBuilder; ui?: PageUi | undefined }) {
  const { locale } = useI18n();
  const copy = getMarketingCopy(locale)?.thankYou;
  return (
    <>
      <section className="section-y pt-32 md:pt-40">
        <div className="container-page max-w-2xl text-center">
          <span className="mx-auto grid size-14 place-items-center rounded-full bg-accent/10 text-accent ring-1 ring-accent/25">
            <CheckCircle2 className="size-7" aria-hidden="true" />
          </span>
          <p className="eyebrow mt-8">{copy?.eyebrow ?? "Enquiry received"}</p>
          <h1 className="display-1 mt-4 text-balance">
            {copy?.title ?? "Thank you for contacting Revolin Roofing"}
          </h1>
          <p className="mt-6 text-base leading-8 text-muted-foreground">
            {copy?.body ??
              "If your message was submitted successfully, our team will review the details and follow up using the contact information you provided. For urgent water entry, call us directly."}
          </p>
          <div className="mt-10 flex flex-col justify-center gap-3 sm:flex-row">
            <Button variant="accent" size="xl" asChild>
              <a
                href={business.phoneHref}
                onClick={() =>
                  trackAnalyticsEvent({ event: "phone_click", link_location: "thank_you" })
                }
              >
                <Phone aria-hidden="true" /> {business.phone}
              </a>
            </Button>
            <Button variant="quiet" size="xl" asChild>
              <a href={path("/services")}>{ui?.common.exploreServices ?? "Explore services"}</a>
            </Button>
            <Button variant="quiet" size="xl" asChild>
              <a href={path("/")}>{ui?.common.home ?? "Home"}</a>
            </Button>
          </div>
        </div>
      </section>
    </>
  );
}

export function FaqPage({ path, ui }: { path: PathBuilder; ui?: PageUi | undefined }) {
  const copy = ui?.corePages.faq;
  return (
    <>
      <PageHero
        eyebrow={copy?.eyebrow ?? "Homeowner guidance"}
        title={copy?.title ?? "Roofing questions, answered without the sales script"}
        body={
          copy?.body ??
          "Practical information about assessment, repair, replacement and roof systems. Project-specific advice follows an on-site review."
        }
        breadcrumbs={
          <Breadcrumbs
            items={[
              { label: ui?.common.home ?? "Home", href: path("/") },
              { label: ui?.common.questions ?? "FAQ" },
            ]}
          />
        }
      />
      <Faq />
      <AssessmentSection ui={ui} />
    </>
  );
}

export function ContactPage({ path, ui }: { path: PathBuilder; ui?: PageUi | undefined }) {
  const copy = ui?.corePages.contact;
  return (
    <>
      <PageHero
        eyebrow={copy?.eyebrow ?? "Contact Revolin Roofing"}
        title={copy?.title ?? "Tell us what you are seeing"}
        body={
          copy?.body ??
          "Share the property location, roof type if known and the concern or project you are considering. The form will only confirm delivery when a configured lead service accepts it."
        }
        breadcrumbs={
          <Breadcrumbs
            items={[
              { label: ui?.common.home ?? "Home", href: path("/") },
              { label: copy?.eyebrow ?? "Contact" },
            ]}
          />
        }
      >
        <div className="flex flex-col gap-3 sm:flex-row">
          <Button variant="accent" size="xl" asChild>
            <a
              href={business.phoneHref}
              onClick={() =>
                trackAnalyticsEvent({ event: "phone_click", link_location: "contact_hero" })
              }
            >
              <Phone aria-hidden="true" /> {business.phone}
            </a>
          </Button>
          <Button variant="outlineDark" size="xl" asChild>
            <a href={`mailto:${business.email}`}>
              <Mail aria-hidden="true" /> {business.email}
            </a>
          </Button>
        </div>
      </PageHero>
      <section className="section-y bg-muted/50">
        <div className="container-page grid gap-12 lg:grid-cols-[0.65fr_1.35fr] lg:gap-16">
          <aside>
            <p className="eyebrow">{copy?.direct ?? "Direct contact"}</p>
            <h2 className="display-2 mt-3">{copy?.basics ?? "Start with the basics"}</h2>
            <dl className="mt-8 space-y-5 text-sm">
              <div>
                <dt className="text-muted-foreground">{copy?.phone ?? "Phone"}</dt>
                <dd className="mt-1 font-semibold">
                  <a href={business.phoneHref} className="hover:text-accent">
                    {business.phone}
                  </a>
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground">{copy?.email ?? "Email"}</dt>
                <dd className="mt-1 font-semibold">
                  <a href={`mailto:${business.email}`} className="hover:text-accent">
                    {business.email}
                  </a>
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground">{copy?.serviceRegion ?? "Service region"}</dt>
                <dd className="mt-1 font-semibold">
                  {copy?.regionValue ?? "Metro Vancouver and the Lower Mainland"}
                </dd>
              </div>
            </dl>
          </aside>
          <QuoteForm />
        </div>
      </section>
    </>
  );
}

export function LegalPage({
  kind,
  path,
  ui,
}: {
  kind: "privacy" | "terms";
  path: PathBuilder;
  ui?: PageUi | undefined;
}) {
  const privacy = kind === "privacy";
  const copy = ui?.corePages.legal;
  const title = privacy
    ? (copy?.privacyTitle ?? "Privacy Policy")
    : (copy?.termsTitle ?? "Website Terms");
  const localizedSections = privacy ? copy?.privacySections : copy?.termsSections;
  return (
    <>
      <PageHero
        eyebrow={copy?.legal ?? "Legal"}
        title={title}
        rotatingVision={false}
        body={
          privacy
            ? (copy?.privacyBody ??
              "How website enquiries, attribution data and essential browser storage are handled.")
            : (copy?.termsBody ??
              "The terms that apply when using Revolin Roofing’s website and general educational content.")
        }
        breadcrumbs={
          <Breadcrumbs
            items={[{ label: ui?.common.home ?? "Home", href: path("/") }, { label: title }]}
          />
        }
      />
      <article className="section-y">
        <div className="container-page max-w-4xl space-y-10 text-sm leading-7 text-muted-foreground">
          {localizedSections ? (
            localizedSections.map((section) => (
              <LegalSection key={section.title} title={section.title}>
                {section.body}
              </LegalSection>
            ))
          ) : privacy ? (
            <PrivacyCopy />
          ) : (
            <TermsCopy />
          )}
          <p className="border-t border-border pt-8 text-xs">
            {copy?.lastUpdated ?? "Last updated: August 10, 2026."}
          </p>
        </div>
      </article>
    </>
  );
}

function PrivacyCopy() {
  return (
    <>
      <LegalSection title="Information you provide">
        When you submit an enquiry, the form may collect your name, phone number, email address,
        postal code, service interest, preferred timeline and message. Submission is available only
        when an approved lead-delivery service is configured.
      </LegalSection>
      <LegalSection title="Attribution and technical information">
        The site may preserve campaign parameters such as UTM values and Google advertising click
        identifiers in session storage so an enquiry can be attributed to its source. Analytics
        events must not contain the private fields entered in the form.
      </LegalSection>
      <LegalSection title="How information is used">
        Enquiry information is used to respond to the roofing request, understand project needs and
        maintain the related business record. We do not describe or authorize any sale of personal
        information through this policy.
      </LegalSection>
      <LegalSection title="Service providers and retention">
        Approved hosting, analytics and lead-delivery providers may process information on Revolin’s
        behalf. Retention and access are limited to legitimate business, legal and security needs.
      </LegalSection>
      <LegalSection title="Your choices">
        You may contact Revolin Group at {business.email} to ask about personal information
        associated with your enquiry. Browser storage can also be cleared through your browser
        settings.
      </LegalSection>
    </>
  );
}

function TermsCopy() {
  return (
    <>
      <LegalSection title="General information">
        Website content is provided for general educational and planning purposes. It is not an
        engineering opinion, building-code determination, inspection certificate or project-specific
        recommendation.
      </LegalSection>
      <LegalSection title="Project scopes">
        Roofing recommendations, price, scheduling, product selections, warranty terms and
        exclusions apply only when stated in an executed project document. Website examples do not
        create a promise for a particular property.
      </LegalSection>
      <LegalSection title="Images and project evidence">
        Website images and project materials are provided for general planning reference. Project
        scopes, warranties and commitments apply only when stated in an executed project document.
      </LegalSection>
      <LegalSection title="External links">
        Links to external websites are provided for context. Their availability, content and privacy
        practices are controlled by their respective operators.
      </LegalSection>
      <LegalSection title="Acceptable use">
        Do not misuse the site, attempt unauthorized access or submit unlawful, deceptive or
        malicious material through its forms.
      </LegalSection>
    </>
  );
}

function LegalSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 className="text-2xl font-semibold text-foreground">{title}</h2>
      <p className="mt-3">{children}</p>
    </section>
  );
}

function AssessmentSection({ ui }: { ui?: PageUi | undefined }) {
  return (
    <section className="section-y bg-primary text-primary-foreground">
      <div className="container-page grid gap-10 lg:grid-cols-[0.7fr_1.3fr] lg:gap-16">
        <div>
          <p className="eyebrow">{ui?.common.nextStep ?? "Next step"}</p>
          <h2 className="display-2 mt-3 text-balance">
            {ui?.corePages.assessment.title ?? "Request a roof assessment"}
          </h2>
          <p className="mt-5 leading-7 text-primary-foreground/70">
            {ui?.corePages.assessment.body ??
              "Give us the property location and a concise description of the roof or concern."}
          </p>
        </div>
        <QuoteForm />
      </div>
    </section>
  );
}
